import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useGraph, useSimulationStore } from '../../../store/simulationStore';
import { fuzzyScore } from './NodeSearch';
import {
  buildTornadoResult,
  findPrimaryOutputNode,
  getSensitivityTargets,
  type InputAssumptionRow,
  type QuantileSet,
  type SensitivityTarget
} from '../utils/tornadoSensitivity';

type SortDirection = 'asc' | 'desc';
type SortField =
  | 'name'
  | 'tags'
  | 'unit'
  | 'p10'
  | 'median'
  | 'p90'
  | 'impact';

interface InputsAssumptionsTableProps {
  isReadOnly?: boolean;
  onOpenNodeOptions: (nodeId: string) => void;
}

const SORT_LABELS: Record<SortField, string> = {
  name: 'Input',
  tags: 'Tags',
  unit: 'Unit',
  p10: 'P10',
  median: 'Median',
  p90: 'P90',
  impact: 'Impact'
};

const ZERO_IMPACT_EPSILON = 1e-9;

const formatNumber = (value: number): string => {
  const abs = Math.abs(value);
  const maximumFractionDigits = abs > 0 && abs < 1 ? 4 : 2;

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits
  });
};

const formatSignedDelta = (value: number | null, unit?: string): string => {
  if (value === null || !Number.isFinite(value)) {
    return '...';
  }

  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const suffix = unit ? ` ${unit}` : '';
  return `${sign}${formatNumber(Math.abs(value))}${suffix}`;
};

const formatValue = (value: number | null | undefined, unit?: string): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '...';
  }

  return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
};

const getTargetSearchScore = (query: string, target: SensitivityTarget): number => {
  const trimmed = query.trim();
  if (!trimmed) {
    return 0;
  }

  return fuzzyScore(trimmed, `${target.name} ${target.isOutput ? 'output' : 'formula'}`);
};

const compareRows = (a: InputAssumptionRow, b: InputAssumptionRow, field: SortField): number => {
  switch (field) {
    case 'name':
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    case 'tags':
      return a.tags.join(', ').localeCompare(b.tags.join(', '), undefined, { sensitivity: 'base' });
    case 'unit':
      return a.unit.localeCompare(b.unit, undefined, { sensitivity: 'base' });
    case 'p10':
      return a.quantiles.p10 - b.quantiles.p10;
    case 'median':
      return a.quantiles.median - b.quantiles.median;
    case 'p90':
      return a.quantiles.p90 - b.quantiles.p90;
    case 'impact':
      return a.impactMagnitude - b.impactMagnitude;
  }
};

const TornadoBar = ({
  inputName,
  inputUnit,
  quantiles,
  targetName,
  delta,
  lowValue,
  highValue,
  baselineValue,
  baselineUnit,
  domainMin,
  domainMax,
  unit,
  error
}: {
  inputName: string;
  inputUnit?: string;
  quantiles: QuantileSet;
  targetName?: string;
  delta: number | null;
  lowValue?: number;
  highValue?: number;
  baselineValue: number | null;
  baselineUnit?: string;
  domainMin: number;
  domainMax: number;
  unit?: string;
  error?: string;
}) => {
  const hasDomain = domainMax > domainMin;
  const valueToPercent = (value: number): number =>
    Math.min(100, Math.max(0, ((value - domainMin) / (domainMax - domainMin)) * 100));
  const baselineLeft =
    baselineValue !== null && Number.isFinite(baselineValue) && hasDomain
      ? valueToPercent(baselineValue)
      : null;
  const segmentStyle = (value: number | undefined): CSSProperties | undefined => {
    if (!Number.isFinite(value) || baselineLeft === null || !hasDomain) {
      return undefined;
    }

    const valueLeft = valueToPercent(value!);
    if (Math.abs(valueLeft - baselineLeft) <= ZERO_IMPACT_EPSILON) {
      return undefined;
    }

    return {
      left: `${Math.min(valueLeft, baselineLeft)}%`,
      width: `${Math.abs(valueLeft - baselineLeft)}%`
    };
  };
  const lowStyle = segmentStyle(lowValue);
  const highStyle = segmentStyle(highValue);
  const deltaText = formatSignedDelta(delta, unit);
  const targetLabel = targetName ?? 'Selected target';
  const baselineText = formatValue(baselineValue, baselineUnit ?? unit);
  const p10InputText = formatValue(quantiles.p10, inputUnit);
  const p90InputText = formatValue(quantiles.p90, inputUnit);
  const lowTargetText = formatValue(lowValue, unit);
  const highTargetText = formatValue(highValue, unit);
  const ariaLabel = error
    ? error
    : `${targetLabel} changes by ${deltaText} when ${inputName} moves from P10 ${p10InputText} to P90 ${p90InputText}. All other inputs are held at median.`;

  return (
    <div className="assumption-tornado" tabIndex={0} aria-label={ariaLabel}>
      <div className="assumption-tornado-chart">
        <div className="assumption-tornado-track" aria-hidden="true">
          {baselineLeft !== null && (
            <span className="assumption-tornado-baseline" style={{ left: `${Math.min(100, Math.max(0, baselineLeft))}%` }} />
          )}
          {lowStyle && <span className="assumption-tornado-fill is-p10" style={lowStyle} />}
          {highStyle && <span className="assumption-tornado-fill is-p90" style={highStyle} />}
        </div>
        <span className="assumption-tornado-value">{deltaText}</span>
      </div>
      <div className="assumption-tornado-popover" role="tooltip">
        {error ? (
          <p className="assumption-tornado-error">{error}</p>
        ) : (
          <>
            <div className="assumption-tornado-popover-header">
              <span>Tornado sensitivity</span>
              <strong>{targetLabel}</strong>
            </div>
            <div className="assumption-tornado-summary">
              <strong>{deltaText}</strong>
              <span>
                target movement when {inputName} moves from P10 to P90.
              </span>
            </div>
            <div className="assumption-tornado-details">
              <div>
                <span>Baseline target</span>
                <strong>{baselineText}</strong>
              </div>
              <div>
                <span>
                  <i className="assumption-tornado-dot is-p10" aria-hidden="true" />
                  P10 input: {p10InputText}
                </span>
                <strong>{lowTargetText}</strong>
              </div>
              <div>
                <span>
                  <i className="assumption-tornado-dot is-p90" aria-hidden="true" />
                  P90 input: {p90InputText}
                </span>
                <strong>{highTargetText}</strong>
              </div>
            </div>
            <p className="assumption-tornado-note">All other inputs are held at their median values.</p>
          </>
        )}
      </div>
    </div>
  );
};

const SortIcon = ({
  field,
  sortField,
  sortDirection
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) {
    return <ArrowUpDown size={12} strokeWidth={2.2} aria-hidden="true" />;
  }

  return sortDirection === 'asc'
    ? <ArrowUp size={12} strokeWidth={2.5} aria-hidden="true" />
    : <ArrowDown size={12} strokeWidth={2.5} aria-hidden="true" />;
};

export const InputsAssumptionsTable = ({
  onOpenNodeOptions
}: InputsAssumptionsTableProps) => {
  const graph = useGraph();
  const setSelection = useSimulationStore((state) => state.setSelection);
  const [sortField, setSortField] = useState<SortField>('impact');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [hideZeroImpact, setHideZeroImpact] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [targetSearchValue, setTargetSearchValue] = useState('');
  const [isTargetMenuOpen, setIsTargetMenuOpen] = useState(false);

  const sensitivityTargets = useMemo(() => getSensitivityTargets(graph), [graph]);

  const selectedTarget = useMemo(() => {
    const selected = selectedTargetId
      ? sensitivityTargets.find((target) => target.id === selectedTargetId)
      : undefined;
    if (selected) {
      return selected;
    }

    const primaryOutputNode = findPrimaryOutputNode(graph);
    return sensitivityTargets.find((target) => target.id === primaryOutputNode?.id) ?? sensitivityTargets[0];
  }, [graph, selectedTargetId, sensitivityTargets]);

  useEffect(() => {
    setTargetSearchValue(selectedTarget?.name ?? '');
  }, [selectedTarget?.name]);

  const filteredSensitivityTargets = useMemo(() => {
    const trimmed = targetSearchValue.trim();
    if (!trimmed || trimmed === selectedTarget?.name) {
      return sensitivityTargets;
    }

    return sensitivityTargets
      .map((target) => ({
        target,
        score: getTargetSearchScore(trimmed, target)
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.target.name.localeCompare(b.target.name, undefined, { sensitivity: 'base' });
      })
      .map(({ target }) => target);
  }, [selectedTarget?.name, sensitivityTargets, targetSearchValue]);

  const tornadoResult = useMemo(
    () => buildTornadoResult(graph, selectedTarget?.id),
    [graph, selectedTarget?.id]
  );

  const { rows, baselineValue, baselineUnit } = tornadoResult;

  const zeroImpactCount = rows.filter((row) => row.impactMagnitude <= ZERO_IMPACT_EPSILON).length;

  const tornadoDomain = useMemo(() => {
    const values = rows.flatMap((row) => [row.impactLowValue, row.impactHighValue])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (baselineValue !== null && Number.isFinite(baselineValue)) {
      values.push(baselineValue);
    }
    if (values.length === 0) {
      return { min: 0, max: 0 };
    }

    let min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) {
      const padding = Math.max(1, Math.abs(min) * 0.05);
      min -= padding;
      max += padding;
    }

    return { min, max };
  }, [baselineValue, rows]);

  const filteredRows = useMemo(() => {
    const nextRows = rows.filter((row) => {
      if (hideZeroImpact && row.impactMagnitude <= ZERO_IMPACT_EPSILON) {
        return false;
      }
      return true;
    });

    return nextRows.sort((a, b) => {
      const comparison = compareRows(a, b, sortField);
      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [hideZeroImpact, rows, sortDirection, sortField]);

  const setSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortDirection('asc');
    setSortField(field);
  }, [sortField]);

  const openNodeOptions = useCallback((nodeId: string) => {
    setSelection(nodeId);
    onOpenNodeOptions(nodeId);
  }, [onOpenNodeOptions, setSelection]);

  const selectSensitivityTarget = useCallback((target: SensitivityTarget) => {
    setSelectedTargetId(target.id);
    setTargetSearchValue(target.name);
    setIsTargetMenuOpen(false);
  }, []);

  const handleTargetKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const firstTarget = filteredSensitivityTargets[0];
      if (firstTarget) {
        event.preventDefault();
        selectSensitivityTarget(firstTarget);
      }
    }

    if (event.key === 'Escape') {
      setTargetSearchValue(selectedTarget?.name ?? '');
      setIsTargetMenuOpen(false);
    }
  }, [filteredSensitivityTargets, selectSensitivityTarget, selectedTarget?.name]);

  const headerButton = (field: SortField) => (
    <button
      type="button"
      className="inputs-assumptions-sort-button"
      onClick={() => setSort(field)}
      aria-label={`Sort by ${SORT_LABELS[field]} ${sortField === field && sortDirection === 'asc' ? 'descending' : 'ascending'}`}
    >
      <span>{SORT_LABELS[field]}</span>
      <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
    </button>
  );

  const headerCell = (field: SortField) => (
    <th
      scope="col"
      aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {headerButton(field)}
    </th>
  );

  return (
    <section className="inputs-assumptions-page" aria-labelledby="inputs-assumptions-title">
      <div className="inputs-assumptions-shell">
        <div className="inputs-assumptions-header">
          <div>
            <h1 id="inputs-assumptions-title">Sensitivity</h1>
          </div>
        </div>

        <div className="inputs-assumptions-target-row">
          <label className="inputs-assumptions-target-select">
            <span>Sensitivity target</span>
            <div className="inputs-assumptions-target-combobox">
              <Search size={14} strokeWidth={2.2} aria-hidden="true" />
              <input
                type="search"
                value={targetSearchValue}
                onChange={(event) => {
                  setTargetSearchValue(event.target.value);
                  setIsTargetMenuOpen(true);
                }}
                onFocus={(event) => {
                  setIsTargetMenuOpen(true);
                  event.currentTarget.select();
                }}
                onBlur={() => {
                  setIsTargetMenuOpen(false);
                  setTargetSearchValue(selectedTarget?.name ?? '');
                }}
                onKeyDown={handleTargetKeyDown}
                disabled={sensitivityTargets.length === 0}
                placeholder={sensitivityTargets.length === 0 ? 'No target outputs' : 'Search targets...'}
                role="combobox"
                aria-expanded={isTargetMenuOpen}
                aria-controls="inputs-assumptions-target-options"
                aria-autocomplete="list"
              />
              {isTargetMenuOpen && sensitivityTargets.length > 0 && (
                <div
                  id="inputs-assumptions-target-options"
                  className="inputs-assumptions-target-menu"
                  role="listbox"
                >
                  {filteredSensitivityTargets.length > 0 ? (
                    filteredSensitivityTargets.map((target) => (
                      <button
                        key={target.id}
                        type="button"
                        role="option"
                        aria-selected={target.id === selectedTarget?.id}
                        className={`inputs-assumptions-target-option ${target.id === selectedTarget?.id ? 'is-selected' : ''}`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectSensitivityTarget(target);
                        }}
                      >
                        <span>{target.name}</span>
                        <span>{target.isOutput ? 'Output' : 'Formula'}</span>
                      </button>
                    ))
                  ) : (
                    <div className="inputs-assumptions-target-empty">No targets match.</div>
                  )}
                </div>
              )}
            </div>
          </label>
          <div className="inputs-assumptions-target-baseline">
            <span>Baseline</span>
            <strong>
              {baselineValue === null
                ? '...'
                : `${formatNumber(baselineValue)}${baselineUnit ? ` ${baselineUnit}` : ''}`}
            </strong>
          </div>
        </div>

        <div className="inputs-assumptions-toolbar">
          <label className="inputs-assumptions-zero-toggle">
            <input
              type="checkbox"
              checked={hideZeroImpact}
              onChange={(event) => setHideZeroImpact(event.currentTarget.checked)}
            />
            <span>Hide zero impact ({zeroImpactCount})</span>
          </label>
        </div>

        <p className="inputs-assumptions-note">
          Impact is deterministic tornado sensitivity for {selectedTarget?.name ?? 'the selected sensitivity target'}: P10 and P90 bars are plotted against the median baseline while all other inputs stay at median.
        </p>

        <div className="inputs-assumptions-table-wrap">
          <table className="inputs-assumptions-table">
            <thead>
              <tr>
                {headerCell('name')}
                {headerCell('tags')}
                {headerCell('unit')}
                {headerCell('p10')}
                {headerCell('median')}
                {headerCell('p90')}
                {headerCell('impact')}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <th scope="row">
                    <button
                      type="button"
                      className="inputs-assumptions-name-button"
                      onClick={() => openNodeOptions(row.id)}
                      title={`Open settings for ${row.name}`}
                    >
                      {row.name}
                    </button>
                  </th>
                  <td className="inputs-assumptions-tags-cell">
                    {row.tags.length > 0 ? (
                      <div className="inputs-assumptions-tags-list">
                        {row.tags.map((tag) => (
                          <span key={tag} className="inputs-assumptions-tag-badge">{tag}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="inputs-assumptions-muted">...</span>
                    )}
                  </td>
                  <td>{row.unit || <span className="inputs-assumptions-muted">...</span>}</td>
                  <td>{formatNumber(row.quantiles.p10)}</td>
                  <td>{formatNumber(row.quantiles.median)}</td>
                  <td>{formatNumber(row.quantiles.p90)}</td>
                  <td>
                    <TornadoBar
                      inputName={row.name}
                      inputUnit={row.unit}
                      quantiles={row.quantiles}
                      targetName={selectedTarget?.name}
                      delta={row.impactDelta}
                      lowValue={row.impactLowValue}
                      highValue={row.impactHighValue}
                      baselineValue={baselineValue}
                      baselineUnit={baselineUnit}
                      domainMin={tornadoDomain.min}
                      domainMax={tornadoDomain.max}
                      unit={row.impactUnit}
                      error={row.impactError}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRows.length === 0 && (
            <div className="inputs-assumptions-empty">
              <p>No inputs match this filter.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
